/**
 * Reads the active structure set live from the session (the same domain
 * object the structure list and settings panel render, projected with
 * `createStructureSetReadModel`). Null-safe: the chat drawer is mounted
 * before a patient is loaded, so this never requires a session.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { environmentState } from '~/state/atoms/engineAtoms';
import type { LiveStructureSet } from './liveState';

export function useLiveStructureSet(): () => LiveStructureSet | undefined {
    const env = useRecoilValue(environmentState);
    const session = useSessionOrNull();
    return useCallback(() => {
        // Read live: `env` is the mutable singleton.
        const uid = env?.structureSet?.sopInstanceUID;
        const set = uid
            ? session?.patient.getStructureSetWithSopInstanceUid(uid)
            : undefined;
        if (!set) {
            return undefined;
        }
        const rm = createStructureSetReadModel(set);
        return {
            uid: rm.dicomUid,
            label: rm.structureSetLabel,
            description: rm.structureSetDescription,
            approvalStatus: rm.approvalStatus,
            rois: rm.roiArray.map((r) => ({
                roiNumber: r.roiNumber,
                name: r.name,
                type: r.interpretedType,
                approvalStatus: r.approvalStatus,
                isApproved: r.isApproved,
                isPointRoi: r.isPointRoi,
                ...(r.pointCoordinatesMm
                    ? {
                          pointMm: [
                              r.pointCoordinatesMm[0],
                              r.pointCoordinatesMm[1],
                              r.pointCoordinatesMm[2],
                          ] as const,
                      }
                    : {}),
            })),
        };
    }, [env, session]);
}
